import type { BoardDoc } from '../doc/BoardDoc';
import type { Item } from '../doc/schema';
import { api } from '../sync/api';
import { deleteAssetLocally } from './assets/localAssets';

/** Server-side storage of a board: what it holds and what nothing references any more. */
export interface SweepResult {
  total: { count: number; bytes: number };
  unused: { count: number; bytes: number };
  /** Asset ids removed (empty for a dry run). */
  removed: string[];
}

/** Asset ids one item references: image, attachment, or a board card's custom cover. */
export function assetIdsOf(item: Item): string[] {
  if (item.type === 'image' || item.type === 'file') return [item.props.assetId];
  if (item.type === 'boardcard' && item.props.coverAssetId) return [item.props.coverAssetId];
  if (item.type === 'sheet') {
    // every layer's pixels; duplicated layers may share an asset
    const ids = new Set<string>();
    for (const l of Object.values(item.props.layers)) if (l.assetId) ids.add(l.assetId);
    return [...ids];
  }
  return [];
}

/** Asset ids the document still references (every item on every layer). */
export function referencedAssetIds(board: BoardDoc): string[] {
  const ids = new Set<string>();
  for (const item of board.index.all()) for (const id of assetIdsOf(item)) ids.add(id);
  return [...ids];
}

/**
 * Reports (apply=false) or removes (apply=true) the board's unused server assets. The server
 * spares uploads younger than an hour. After a removal the undo history is cleared: bringing a
 * deleted image back would point at a file that no longer exists.
 */
export async function sweepUnusedAssets(board: BoardDoc, apply: boolean): Promise<SweepResult> {
  const res = await api.post<SweepResult>(`/boards/${board.boardId}/assets/sweep`, { keep: referencedAssetIds(board), apply });
  if (apply && res.removed.length) {
    await Promise.all(res.removed.map((id) => deleteAssetLocally(id)));
    board.undo.clear();
  }
  return res;
}
